/**
 * Cross-tab activity coordination via `BroadcastChannel`. Tabs post an
 * `"activity"` message when the user interacts with this tab; receiving
 * tabs reset their local inactivity timer. `"signed-out"` lets the
 * source tab tell its peers to also tear down (so a timer fired in one
 * tab does not leave others on a stale session).
 *
 * `BroadcastChannel` is supported in all evergreen browsers we ship to.
 * In environments without it (older Safari, jsdom), the wrapper returns
 * no-op `post` / `subscribe` stubs — each tab times out independently;
 * acceptable degradation for a security feature.
 *
 * @agentic connections
 */

import type { AuthResetReason } from "@/app/auth/notifications";

/** Channel name shared across tabs. @agentic connections */
const CHANNEL_NAME = "connections-activity";

/** Cross-tab message shape. @agentic connections */
export type InactivityMessage =
  | { type: "activity"; at: number }
  | { type: "signed-out"; reason: AuthResetReason };

/** Public surface of the wrapper. @agentic connections */
export type InactivityBroadcast = {
  post: (message: InactivityMessage) => void;
  subscribe: (handler: (message: InactivityMessage) => void) => () => void;
  close: () => void;
};

/**
 * Construct an inactivity broadcast wrapper. Falls back to no-op stubs
 * when `BroadcastChannel` is unavailable.
 * @agentic connections
 */
export const createInactivityBroadcast = (): InactivityBroadcast => {
  if (typeof BroadcastChannel === "undefined") {
    return {
      post: () => {},
      subscribe: () => () => {},
      close: () => {},
    };
  }

  let channel: BroadcastChannel | null = new BroadcastChannel(CHANNEL_NAME);

  return {
    post: (message: InactivityMessage) => {
      if (!channel) return;
      try {
        channel.postMessage(message);
      } catch (error) {
        // Channel might have been closed by an unrelated teardown.
        console.error("[inactivityBroadcast] post failed", error);
      }
    },
    subscribe: (handler) => {
      if (!channel) return () => {};
      const listener = (event: MessageEvent<InactivityMessage>) => {
        handler(event.data);
      };
      channel.addEventListener("message", listener);
      return () => {
        if (channel) channel.removeEventListener("message", listener);
      };
    },
    close: () => {
      try {
        channel?.close();
      } catch {
        // Ignore.
      }
      channel = null;
    },
  };
};
